import { useState, useEffect } from 'react';
import api from '../../utils/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { toast } from 'sonner';
import { Link as LinkIcon, WhatsappLogo } from '@phosphor-icons/react';

const RECEIPT_TYPE_OPTIONS = [
  { v: 'advance', l: 'Customer Advance' },
  { v: 'quotation', l: 'Against Quotation / JO' },
  { v: 'adjustment', l: 'Adjustment' },
];

export function GeneratePaymentLinkDialog({
  open,
  onOpenChange,
  prefill = {},
  onCreated,
}) {
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    customer_name: '',
    customer_mobile: '',
    amount: '',
    jo_number: '',
    quotation_number: '',
    description: '',
    expires_in_hours: 72,
    send_via_whatsapp: true,
    receipt_type: 'advance',
    remarks: '',
  });

  // Re-sync form whenever the dialog is (re)opened with a new prefill.
  useEffect(() => {
    if (open) {
      const inferredType = prefill.receipt_type
        || (prefill.jo_number || prefill.quotation_number ? 'quotation' : 'advance');
      setForm({
        customer_name: prefill.customer_name || '',
        customer_mobile: prefill.customer_mobile || '',
        amount: prefill.amount ? String(prefill.amount) : '',
        jo_number: prefill.jo_number || '',
        quotation_number: prefill.quotation_number || '',
        description: prefill.description || '',
        expires_in_hours: 72,
        send_via_whatsapp: true,
        receipt_type: inferredType,
        remarks: prefill.remarks || '',
      });
    }
    // We intentionally only re-sync on open transitions, not on every prefill mutation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const submit = async () => {
    if (!form.customer_name || !form.customer_mobile || !form.amount) {
      toast.error('Customer name, mobile and amount are required');
      return;
    }
    const mob = String(form.customer_mobile).replace(/\D/g, '');
    if (mob.length < 10) {
      toast.error('Enter a valid 10-digit mobile');
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post('/api/admin/payment-links', {
        ...form,
        amount: Number(form.amount),
      });
      toast.success(data?.message || 'Payment link created');
      onCreated && onCreated(data.payment_link);
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create payment link');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="gen-payment-link-dialog" className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <LinkIcon size={20} weight="bold" />
            Generate Payment Link
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Customer Name *</Label>
              <Input
                data-testid="pl-cust-name"
                value={form.customer_name}
                onChange={(e) => setForm({ ...form, customer_name: e.target.value })}
              />
            </div>
            <div>
              <Label>Customer Mobile *</Label>
              <Input
                data-testid="pl-cust-mobile"
                value={form.customer_mobile}
                onChange={(e) => setForm({ ...form, customer_mobile: e.target.value })}
                placeholder="10-digit"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Amount (₹) *</Label>
              <Input
                data-testid="pl-amount"
                type="number"
                value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })}
              />
            </div>
            <div>
              <Label>Receipt Type *</Label>
              <Select
                value={form.receipt_type}
                onValueChange={(v) => setForm({ ...form, receipt_type: v })}
              >
                <SelectTrigger data-testid="pl-receipt-type">
                  <SelectValue placeholder="Pick type" />
                </SelectTrigger>
                <SelectContent>
                  {RECEIPT_TYPE_OPTIONS.map((o) => (
                    <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[10px] text-zinc-500 mt-1">
                {form.receipt_type === 'advance'
                  ? 'Will land in Customer Advance register on payment.'
                  : form.receipt_type === 'adjustment'
                  ? 'Will be recorded as an adjustment against ledger.'
                  : 'Will settle against the linked quotation / JO.'}
              </p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>JO Number</Label>
              <Input
                data-testid="pl-jo"
                value={form.jo_number}
                onChange={(e) => setForm({ ...form, jo_number: e.target.value })}
                disabled={form.receipt_type === 'advance'}
              />
            </div>
            <div>
              <Label>Quotation No</Label>
              <Input
                data-testid="pl-quot"
                value={form.quotation_number}
                onChange={(e) => setForm({ ...form, quotation_number: e.target.value })}
                disabled={form.receipt_type === 'advance'}
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Expires In (hours)</Label>
              <Input
                data-testid="pl-expiry"
                type="number"
                value={form.expires_in_hours}
                onChange={(e) => setForm({ ...form, expires_in_hours: Number(e.target.value || 72) })}
              />
            </div>
            <div>
              <Label>Description</Label>
              <Input
                data-testid="pl-desc"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="e.g. Advance for sliding windows"
              />
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm cursor-pointer mt-2">
            <input
              data-testid="pl-send-whatsapp"
              type="checkbox"
              checked={form.send_via_whatsapp}
              onChange={(e) => setForm({ ...form, send_via_whatsapp: e.target.checked })}
            />
            <WhatsappLogo size={18} className="text-emerald-600" />
            <span>Also send the payment link via WhatsApp</span>
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>Cancel</Button>
          <Button
            data-testid="pl-submit"
            onClick={submit}
            disabled={loading}
            className="bg-orange-500 hover:bg-orange-600 text-white"
          >
            {loading ? 'Generating...' : 'Generate'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
